import { FC, useEffect, useState } from "react"
import { LoadingDots } from "@components/ui"
import { Grid } from "@theme-ui/components"
import { ProductCardProps } from "@components/common/ProductCard"
import { ProductCardDemo, ProductCard } from "@components/common"
import { IncludedItem, Product } from "@lib/swell/storefront-data-hooks/src/types"
import Link from "next/link"
import { getPrice } from "@lib/swell/storefront-data-hooks/src/utils/product"

import {
  getCollection,
  getProduct,
} from "@lib/swell/storefront-data-hooks/src/api/operations-swell"
import builderConfig from "@config/builder"
import Image from "next/image"
interface HighlightedCardProps extends Omit<ProductCardProps, "product"> {
  index: number
}

export interface ProductCardsWithDetailsProps {
  products?: any[]
  productsList?: Array<{ product: string }>
  collection?: string | any
  offset: number
  limit: number
  cardProps: Omit<ProductCardProps, "product">
  highlightCard?: HighlightedCardProps
}

export const ProductCardsWithDetails: FC<ProductCardsWithDetailsProps> = ({
  products: initialProducts,
  collection,
  productsList,
  offset = 0,
  limit = 10,
  cardProps,
  highlightCard,
}) => {
  const [products, setProducts] = useState(initialProducts || [])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const getProducts = async () => {
      setLoading(true)
      const promises = productsList!
        .map((entry) => entry.product)
        .filter((handle: string | undefined) => typeof handle === "string")
        .map(async (handle: string) => {
          return await getProduct({ slug: handle })
        })
      const result = await Promise.all(promises)
      setProducts(result)
      setLoading(false)
    }
    if (productsList && !initialProducts) {
      getProducts()
    }
  }, [productsList, initialProducts])

  useEffect(() => {
    const fetchCollection = async () => {
      setLoading(true)
      const result = await getCollection(builderConfig, {
        handle: collection,
      })
      setProducts(result.products)
      setLoading(false)
    }
    if (typeof collection === "string" && !initialProducts) {
      fetchCollection()
    }
  }, [collection])

  if (loading) {
    return <LoadingDots />
  }

  const ProductComponent: any = process.env.IS_DEMO ? ProductCardDemo : ProductCard
  return (
    <div className="bg-white">
      <div className="">
        <h2 className="sr-only">Products</h2>

        <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-10 lg:grid-cols-3 lg:gap-x-8">
          {products.map((product: Product) => (
            <div
              key={product.id}
              className="group relative flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white"
            >
              <div className="aspect-h-2 aspect-w-3 bg-white group-hover:opacity-75 ">
                {product.images[0] && (
                  <Image
                    fill={true}
                    src={product.images[0]?.src}
                    alt={product.images[0]?.metadata}
                    className="h-full w-full object-contain object-center"
                  />
                )}
              </div>
              <div className="flex flex-1 flex-col space-y-2 p-4">
                <h3 className="text-md font-medium text-gray-900">
                  <Link href={`/product/${product.slug}/`} legacyBehavior>
                    <div>
                      <span aria-hidden="true" className="absolute inset-0" />
                      {product.name}
                    </div>
                  </Link>
                </h3>
                {product.content?.included_items ? (
                  <>
                    <h4 className="text-sm font-medium text-gray-900">Includes</h4>
                    <div className="prose prose-sm mt-4 text-gray-500">
                      <ul role="list">
                        {product.content.included_items.map(({ item }: IncludedItem) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </>
                ) : (
                  <></>
                )}
                <div className="flex flex-1 flex-col justify-end">
                  {product.sale ? (
                    <div>
                      <p className="text-base font-medium text-gray-900 line-through">
                        {getPrice(product.orig_price, "USD")}
                      </p>
                      <div className="flex space-x-2">
                        <p className="font-medium text-gray-900 text-red-600">
                          {getPrice(product.price, "USD")}
                        </p>
                        <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                          SAVE {getPrice(product.orig_price - product.price, "USD")}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-base font-medium text-gray-900">
                      {getPrice(product.price, "USD")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
